import { get, remove, cancellableGet, cancellablePost, CancellablePromiseWrapper } from '../../fetch-interceptor';

export type AuditLogQuery = {
  // Parent entity filters
  entityTypes?: string[];  // Parent entity types (Project, Portfolio, Program, etc.)
  entityIds?: string[];    // Parent entity IDs
  
  // Subentity filters
  subEntityTypes?: string[];  // Subentity types (Task, Risk, Issue, etc.) - can include parent entity types for main entity filtering
  subEntityIds?: string[];    // Subentity IDs
  
  // Event and user filters
  eventTypes?: string[];
  userIds?: string[];
  
  // Date filters
  from?: string; // ISO string expected by backend (DateTimeOffset)
  to?: string;   // ISO string expected by backend (DateTimeOffset)
  
  // Pagination and search
  skip?: number;
  take?: number;
  search?: string;
  searchMode?: 'exact' | 'partial'; // Search mode: exact (fast, default) or partial (flexible)
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
};

export type AttributeDeltaResponse = {
  oldValue?: any;
  newValue?: any;
  changeType: string;
  changeDescription?: string;
  fieldInfo?: FieldDisplayInfo;
};

export type FieldDisplayInfo = {
  name: string;
  label: string;
  type: string;
  displaySettings?: Record<string, any>;
};

export type AuditLogItem = {
  id: string;
  entityType: string;
  eventType: string;
  fullEventName: string;
  timeStamp: string;
  created: string;
  correlationId?: string;
  initiatorUserId?: string;
  initiatorFullName?: string;
  initiatorLogonAccount?: string;
  /** ID of the main entity (Project, Portfolio, Program, etc.). For subentities, this is the parent entity ID */
  entityId?: string;
  entityName?: string;
  /** ID of the subentity (Task, Risk, Issue, etc.) if this is a subentity event. Null for main entity events */
  subEntityId?: string;
  /** Type of the subentity (task, risk, issue, etc.) if this is a subentity event. Null for main entity events */
  subEntityType?: string;
  changes?: Record<string, AttributeDeltaResponse>;
};

export type AuditLogListResponse = AuditLogItem[];

// Helper function to normalize entity type from client format to server format
// Client uses 'externaltask' but server expects 'task'
const normalizeEntityType = (entityType: string): string => {
  return entityType.toLowerCase() === 'externaltask' ? 'task' : entityType;
};

// Helper function to clean query parameters - removes empty arrays and normalizes entity types
const cleanQueryParams = (query: AuditLogQuery): AuditLogQuery => {
  const cleanedQuery = { ...query };
  
  // Normalize entity types: convert 'externaltask' to 'task'
  if (cleanedQuery.entityTypes && cleanedQuery.entityTypes.length > 0) {
    cleanedQuery.entityTypes = cleanedQuery.entityTypes.map(normalizeEntityType);
  }
  
  if (cleanedQuery.subEntityTypes && cleanedQuery.subEntityTypes.length > 0) {
    cleanedQuery.subEntityTypes = cleanedQuery.subEntityTypes.map(normalizeEntityType);
  }
  
  // Remove empty arrays to prevent sending empty parameters
  if (cleanedQuery.entityTypes && cleanedQuery.entityTypes.length === 0) {
    delete cleanedQuery.entityTypes;
  }
  
  if (cleanedQuery.entityIds && cleanedQuery.entityIds.length === 0) {
    delete cleanedQuery.entityIds;
  }
  
  if (cleanedQuery.subEntityTypes && cleanedQuery.subEntityTypes.length === 0) {
    delete cleanedQuery.subEntityTypes;
  }
  
  if (cleanedQuery.subEntityIds && cleanedQuery.subEntityIds.length === 0) {
    delete cleanedQuery.subEntityIds;
  }
  
  if (cleanedQuery.eventTypes && cleanedQuery.eventTypes.length === 0) {
    delete cleanedQuery.eventTypes;
  }
  
  if (cleanedQuery.userIds && cleanedQuery.userIds.length === 0) {
    delete cleanedQuery.userIds;
  }
  
  return cleanedQuery;
};

export const AuditLogApi = {
  // Single endpoint that supports all filter combinations
  getAll: (q: AuditLogQuery) => cancellablePost<AuditLogListResponse>('api/auditlogs', cleanQueryParams(q)),
};
